import React from 'react'
import { useIntl } from 'react-intl'
import { PlayCircleOutlineRounded } from '@mui/icons-material'
import { Divider, FormHelperText, IconButton, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import { Body1, BoldText } from '../../../../../layouts/TextStyles'
import DndIcon from '../../../../../assets/icons/Dnd.svg'
import ConfirmDelete from '../../../../../components/dialogs/ConfirmDelete'
import { CustomRichTextEditor } from '../../../../../components/customRichTextEditor'
import Options from '../components/Options'
import ComplianceItems from '../components/ComplianceItems'
import { QuestionState } from '../../../../../features/types'

interface Props {
    question: QuestionState
    index: number
    key?: number | string
    innerRef?: any
    snapshot?: any
    draggableProps?: any
    dragHandleProps?: any
    handleDelete?: (id: number) => void
    openDialog?: (question: QuestionState) => void
    formik?: any
}

const SingleQuestion: React.FC<Props> = ({
    formik,
    question,
    index,
    handleDelete,
    draggableProps,
    dragHandleProps,
    innerRef,
    snapshot,
    openDialog,
}) => {
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const styles = {
        helperText: {
            color: 'red',
            ml: 1,
        },

        textEditor: {
            minWidth: '200px',
            width: '100%',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '8px',
            m: 1,
        },

        questionInput: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexGrow: 1,
        },

        deleteBox: {
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },

        dndIconBox: {
            padding: '8px',
            borderRadius: '50%',
            background: snapshot.isDragging ? theme.palette.grey[100] : '#fff',
            '&:hover': {
                background: theme.palette.grey[100],
            },
        },

        questionDndBox: {
            display: 'grid',
            gridTemplateColumns: 'auto',
            alignItems: 'start',
            gap: '8px',
        },

        questionBox: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'start',
            mb: 2,
            mr: 2,
        },

        row: {
            display: 'grid',
            alignItems: 'start',
            gridTemplateColumns: 'repeat(2, 50%)',
            pt: 3,
            pb: 3,
        },
    }

    const handleChangeQuestionType = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const questionTypeEnum = (event.target as HTMLInputElement).value

        formik.setFieldValue(
            `questions[${index}].questionTypeEnum`,
            questionTypeEnum,
        )

        if (questionTypeEnum === 'ONE_ANSWER') {
            resetCorrectAnswers(question)
        }

        if (questionTypeEnum === 'MATCHING') {
            resetCorrectAnswers(question)

            if (!question.complianceItems?.length) {
                formik.setFieldValue(
                    `questions[${index}].complianceItems`,
                    [1, 2].map((item) => ({
                        complianceItemId: Date.now() + item,
                        content: '',
                        position: 0,
                        correctAnswerIndexes: [],
                    })),
                )
            }
        }
    }

    const resetCorrectAnswers = (question: QuestionState) => {
        question.answers.forEach((_, i) => {
            formik.handleChange({
                target: {
                    name: `questions[${index}].answers[${i}].correct`,
                    value: false,
                },
            })
        })
    }

    return (
        <Box
            ref={innerRef}
            {...draggableProps}
        >
            <Box>
                <FormControl>
                    <FormLabel id="demo-row-radio-buttons-group-label">
                        <Body1>{formatMessage({ id: 'questionType' })}</Body1>
                    </FormLabel>

                    <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={question.questionTypeEnum}
                        onChange={handleChangeQuestionType}
                    >
                        <FormControlLabel
                            value="ONE_ANSWER"
                            control={<Radio />}
                            label={formatMessage({ id: 'ONE_ANSWER' })}
                        />
                        <FormControlLabel
                            value="MULTI_SELECT"
                            control={<Radio />}
                            label={formatMessage({ id: 'MULTI_SELECT' })}
                        />
                        <FormControlLabel
                            value="MATCHING"
                            control={<Radio />}
                            label={formatMessage({ id: 'MATCHING' })}
                        />
                    </RadioGroup>
                </FormControl>
            </Box>

            <Box sx={styles.row}>
                <Box sx={styles.questionBox}>
                    <Box sx={styles.questionDndBox}>
                        <Box
                            {...dragHandleProps}
                            sx={styles.dndIconBox}
                        >
                            <img
                                alt={'dnd'}
                                src={DndIcon}
                            />
                        </Box>

                        <IconButton onClick={() => openDialog(question)}>
                            <PlayCircleOutlineRounded />
                        </IconButton>

                        <Box sx={styles.deleteBox}>
                            <ConfirmDelete
                                confirmAction={() => handleDelete(index)}
                            />
                        </Box>
                    </Box>

                    <Box sx={styles.questionInput}>
                        <Box
                            p={1}
                            pb={0}
                        >
                            <BoldText>
                                {index + 1} -{' '}
                                {formatMessage({ id: 'question' })}
                            </BoldText>
                        </Box>

                        <Box
                            className="CustomEditor"
                            sx={styles.textEditor}
                        >
                            <CustomRichTextEditor
                                value={question.content}
                                onEditorChange={(text) => {
                                    formik.handleChange({
                                        target: {
                                            name: `questions[${index}].content`,
                                            value: text,
                                        },
                                    })
                                }}
                                editable
                                inline
                            />
                        </Box>

                        <FormHelperText sx={styles.helperText}>
                            {formik.errors?.questions?.[index]?.content}
                        </FormHelperText>
                    </Box>
                </Box>

                <Box>
                    <Options
                        formik={formik}
                        questionIndex={index}
                        question={question}
                        resetCorrectAnswers={resetCorrectAnswers}
                    />

                    {question.questionTypeEnum === 'MATCHING' && (
                        <ComplianceItems
                            formik={formik}
                            questionIndex={index}
                            question={question}
                        />
                    )}
                </Box>
            </Box>
            <Divider />
        </Box>
    )
}

export default SingleQuestion
